import { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Company } from '../../types/intellijetnet'
import { CompaniesService } from '../../services/intellijetnet/companies.service'
import { usePopoverEditor } from '../../hooks/usePopoverEditor'

interface InlineCompanyEditorProps {
  /** Aircraft ID to fetch associated companies */
  aircraftId: number
  /** Current company ID (if selected from dropdown) */
  companyId: number | null
  /** Current custom text value */
  customValue: string | null
  /** Field name for display purposes */
  fieldName: 'Owner' | 'Broker'
  /** Called when user saves - receives both company ID and custom value */
  onSave: (companyId: number | null, customValue: string | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
}

/**
 * Inline editor for Owner/Broker fields in market tables.
 * Shows a popover with:
 * - Radio buttons to select from associated companies
 * - Custom text input option
 * - Click outside to save, Escape to cancel
 */
export default function InlineCompanyEditor({
  aircraftId,
  companyId,
  customValue,
  fieldName,
  onSave,
  onCancel
}: InlineCompanyEditorProps) {
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [mode, setMode] = useState<'company' | 'custom'>(customValue ? 'custom' : 'company')
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(companyId)
  const [customText, setCustomText] = useState(customValue || '')
  
  const popoverRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const savingRef = useRef(false)
  const modeRef = useRef(mode)
  const selectedCompanyIdRef = useRef(selectedCompanyId)
  const customTextRef = useRef(customText)

  // Keep refs in sync
  useEffect(() => { modeRef.current = mode }, [mode])
  useEffect(() => { selectedCompanyIdRef.current = selectedCompanyId }, [selectedCompanyId])
  useEffect(() => { customTextRef.current = customText }, [customText])

  // Fetch companies for this aircraft
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true)
        const data = await CompaniesService.getByAircraftId(aircraftId)
        setCompanies(data)
      } catch (error) {
        // Error fetching companies
      } finally {
        setLoading(false)
      }
    }
    fetchCompanies()
  }, [aircraftId])

  const doSave = useCallback(async () => {
    if (savingRef.current) return

    const currentMode = modeRef.current
    const currentCompanyId = selectedCompanyIdRef.current
    const currentCustomText = customTextRef.current.trim()

    // Determine new values based on mode
    const newCompanyId = currentMode === 'company' ? currentCompanyId : null
    const newCustomValue = currentMode === 'custom' && currentCustomText ? currentCustomText : null

    // Check if anything changed
    const companyChanged = newCompanyId !== companyId
    const customChanged = newCustomValue !== (customValue || null)

    if (!companyChanged && !customChanged) {
      onCancel()
      return
    }

    savingRef.current = true
    setSaving(true)
    try {
      await onSave(newCompanyId, newCustomValue)
    } catch (error) {
      savingRef.current = false
      setSaving(false)
    }
  }, [companyId, customValue, onSave, onCancel])

  const { position } = usePopoverEditor({
    onClickOutside: doSave,
    onCancel,
    popoverRef,
    anchorRef,
    popoverWidth: 300,
    popoverHeight: 300,
    centerOnAnchor: true,
    attachDelay: 0
  })

  const handleClear = () => {
    setMode('company')
    setSelectedCompanyId(null)
    setCustomText('')
  }

  const popoverContent = (
    <div
      ref={popoverRef}
      className="fixed z-[9999] bg-white border border-green-500 rounded-lg shadow-lg p-3 min-w-[250px] max-w-[300px]"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' as const }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-xs font-medium text-gray-700 mb-2">Select {fieldName}</div>

      {loading ? (
        <div className="text-sm text-gray-500 py-2">Loading companies...</div>
      ) : (
        <div className="space-y-2">
          {/* Company options */}
          {companies.length > 0 && (
            <div className="max-h-32 overflow-y-auto space-y-1 border-b border-gray-200 pb-2 mb-2">
              {companies.map((company) => (
                <label key={company.company_id} className="flex items-center p-1.5 hover:bg-green-50 rounded cursor-pointer">
                  <input
                    type="radio"
                    name={`${fieldName}-selection`}
                    checked={mode === 'company' && selectedCompanyId === company.company_id}
                    onChange={() => { setMode('company'); setSelectedCompanyId(company.company_id) }}
                    className="mr-2 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-900 truncate">{company.company_name}</span>
                </label>
              ))}
            </div>
          )}

          {/* Custom text option */}
          <div className="space-y-1">
            <label className="flex items-center p-1.5 hover:bg-green-50 rounded cursor-pointer">
              <input
                type="radio"
                name={`${fieldName}-selection`}
                checked={mode === 'custom'}
                onChange={() => setMode('custom')}
                className="mr-2 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-gray-900">Custom</span>
            </label>
            {mode === 'custom' && (
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder={`Enter custom ${fieldName.toLowerCase()} name`}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
              />
            )}
          </div>

          {/* Clear button */}
          <div className="pt-2 border-t border-gray-200 mt-2">
            <button
              type="button"
              onClick={handleClear}
              disabled={saving}
              className="w-full px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            >
              Clear
            </button>
          </div>

          {saving && (
            <div className="text-xs text-gray-500 text-center mt-2">Saving...</div>
          )}
        </div>
      )}
    </div>
  )

  return (
    <>
      <span ref={anchorRef} />
      {ReactDOM.createPortal(popoverContent, document.body)}
    </>
  )
}
